// =============================================================================
// ui/charts/CustomChart.tsx — one custom widget's chart.
// -----------------------------------------------------------------------------
// All the arithmetic is in `domain/customChart` (via the selector seam); this
// draws what it returns and nothing more.
//
// ⚠ `preserveAspectRatio="none"`, as the old client does — the SVG stretches to
// the card's width. Two consequences, both handled:
//
//   • strokes distort, so every stroked element carries `vector-effect`
//   • text distorts too, and there is no `vector-effect` for glyphs
//
// 🚨 AND YET THE TEXT IS INSIDE THE SVG HERE, unlike `EquityCurve`, WHICH PULLS
// ITS LABELS OUT INTO HTML. That is not an inconsistency — it is the old client,
// which puts these labels in `<text>` and accepts the squash. Pulling them out
// would be an improvement, and an improvement is a divergence during a phase
// whose gate is parity. The difference is deliberate and belongs in a later
// decision, not smuggled in here.
// =============================================================================

import {
  buildChartGeometry, type SeriesDef, type SeriesPoint,
} from '../../selectors/customAnalytics';
import { metricValue } from '../format';

export interface CustomChartProps {
  points: readonly SeriesPoint[];
  series: readonly SeriesDef[];
  /** For the accessible name — the widget's own headline. */
  label: string;
}

export function CustomChart({ points, series, label }: CustomChartProps) {
  const g = buildChartGeometry(points, series);
  if (!g) {
    return (
      <div className="cw-nodata">No trades in this period.</div>
    );
  }

  return (
    <svg
      className="cw-chart"
      viewBox={`0 0 ${g.width} ${g.height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      {/* The zero line, and only where zero is on the scale. */}
      {g.zeroY !== null && (
        <line
          x1={g.padL} y1={g.zeroY.toFixed(1)} x2={(g.width - g.padR).toFixed(1)} y2={g.zeroY.toFixed(1)}
          stroke="var(--border-2)" strokeWidth={1} vectorEffect="non-scaling-stroke"
        />
      )}

      {/* 1) bars behind everything. */}
      {g.bars.map((b, i) => (
        <rect
          key={i}
          x={b.x.toFixed(1)} y={b.y.toFixed(1)}
          width={b.width.toFixed(1)} height={b.height.toFixed(1)}
          fill={b.color} rx={1.5}
        />
      ))}

      {/* 2) area fills — semi-transparent, so bars underneath still read. */}
      {g.lines.filter((l) => l.area).map((l) => (
        <polygon key={`a-${l.key}`} points={l.area as string} fill={l.color} fillOpacity={0.16} stroke="none" />
      ))}

      {/* 3) line strokes on top of both. */}
      {g.lines.map((l) => (
        <polyline
          key={`l-${l.key}`}
          points={l.points} fill="none" stroke={l.color} strokeWidth={2}
          vectorEffect="non-scaling-stroke" strokeLinejoin="round"
        />
      ))}

      {/* 4) the latest value at each line's right edge. */}
      {g.values.map((v, i) => (
        <text
          key={`v-${i}`}
          x={v.x} y={v.y.toFixed(1)} fill={v.color} fontSize={10} fontWeight={700}
          fontFamily="var(--mono)" dominantBaseline="middle"
        >
          {metricValue(v.value, v.unit)}
        </text>
      ))}

      {/* 5) each axis' min and max. */}
      {g.axes.map((a, i) => (
        <text
          key={`x-${i}`}
          x={a.x.toFixed(1)} y={a.y.toFixed(1)} fill={a.color} fontSize={9}
          fontFamily="var(--mono)" textAnchor={a.anchor} dominantBaseline="middle"
        >
          {metricValue(a.value, a.unit)}
        </text>
      ))}

      {/* 6) first / middle / last period. */}
      {g.xLabels.map((x, i) => (
        <text
          key={`d-${i}`}
          x={x.x.toFixed(1)} y={g.height - 6} fill="var(--text-3)" fontSize={9}
          textAnchor={x.anchor} fontFamily="var(--mono)"
        >
          {x.text}
        </text>
      ))}
    </svg>
  );
}
